import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import FreeForm from "@widgets/FreeForm";
import SnGrid from "@shared/components/Grid/SnGrid";
import SnResizable from "@shared/components/SnResizable";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitPopUp } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import { SnButton } from "@shared/components/SnButton";

interface Props {
  modalRef?: any;
  Menuprops?: menuInfo;
  initialParams?: {
    cd_pjt?: string;
    cd_unit?: string;
    cd_item?: string;
    nm_pjt?: string;
    nm_st_pjt?: string;
  };
}

const SN_PMS_PJT_REG_004_POP_002 = forwardRef(
  ({ modalRef, Menuprops, initialParams }: Props, ref) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const freeFormRef = useRef<any>(null);
    const gridRef = useRef<any>(null);

    const [formData, setFormData] = useState<any>({});
    const [searchControls, setSearchControls] = useState<any[]>([]);
    const [freeform1Controls, setFreeform1Controls] = useState<any[]>([]);
    const [gridColumns, setGridColumns] = useState<any[]>([]);
    const [gridData, setGridData] = useState<any[]>([]);

    const defaultPanelSize = {
      panel1: "60%",
      panel2: "40%",
    };
    const [panelSizes, setPanelSizes] = useState<{
      [key: string]: string | number;
    }>(defaultPanelSize);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        const changes = gridRef.current?.getChange?.();
        const hasChange = changes?.modifiedRowcount > 0;
        return {
          flag: true,
          hasGridChanges: !!hasChange,
        };
      },
      getSelectedValue: async () => {
        const ok = await onConfirm();
        if (ok) return "ok";
        return null;
      },
      onConfirm,
      onCancel,
    }));

    useEffect(() => {
      const fetchInit = async () => {
        try {
          const paramSeed = {
            ...(initialParams || {}),
            ...(modalRef?.params || {}),
            nm_pjt:
              (modalRef?.params?.nm_pjt ?? initialParams?.nm_pjt ?? "") || "",
            nm_st_pjt:
              (modalRef?.params?.nm_st_pjt ?? initialParams?.nm_st_pjt ?? "") ||
              "",
          };
          const rtn: any = await getInitPopUp("SN_PMS_PJT_REG_004_POP_002", {
            cd_menu: Menuprops?.cd_menu || modalRef?.cd_menu,
            cd_firm: userInfo?.cd_firm,
            cd_item: paramSeed?.cd_item,
          });
          const mergedFormData = {
            ...(rtn.formData || {}),
            ...paramSeed,
          };
          setFormData(mergedFormData);
          setSearchControls(rtn.form?.search || rtn.formControlData);
          setFreeform1Controls(rtn.form?.freeform1);
          setGridColumns(rtn.grid?.grid1);
          setGridData(rtn.gridData || []);
          if (initialParams?.cd_pjt || initialParams?.cd_unit) {
            setTimeout(() => onSearch(), 0);
          }
        } catch (error) {}
      };
      fetchInit();
    }, [Menuprops, modalRef, userInfo, initialParams]);

    const onGrid1BtnsClick = async (id: string) => {
      if (id === "addRow") {
        onAddRow();
      }
      if (id === "deleteRow") {
        onDeleteRow();
      }
      if (id === "modifyRow") {
        onModifyRow();
      }
    };

    const buildDefaultRow = (baseRow: any = {}) => {
      const paramSeed = {
        ...(initialParams || {}),
        ...(modalRef?.params || {}),
      };

      const currentRows = gridRef.current?.getRowData?.() ?? gridData ?? [];
      const firstRow = currentRows[0] || {};
      const maxLine =
        currentRows.reduce(
          (acc: number, r: any) => Math.max(acc, Number(r?.no_line) || 0),
          0
        ) || 0;

      const defaults = {
        state: "insert",
        cd_firm: userInfo?.cd_firm,
        cd_pjt: paramSeed.cd_pjt,
        cd_unit: paramSeed.cd_unit,
        cd_item: baseRow.cd_item ?? paramSeed.cd_item,
        no_hst: paramSeed.no_hst ?? formData?.no_hst ?? firstRow?.no_hst ?? "",
        no_line:
          baseRow.no_line != null && baseRow.no_line !== ""
            ? baseRow.no_line
            : maxLine + 1,
        fg_eco: baseRow.fg_eco ?? "I",
        no_rev: firstRow?.no_rev,
        cd_cb: firstRow?.cd_cb,
        no_cbs: firstRow?.no_cbs,
        cd_dummy01: firstRow?.cd_dummy01,
        cd_dummy02: firstRow?.cd_dummy02,
        cd_dummy03: firstRow?.cd_dummy03,
        cd_dummy04: firstRow?.cd_dummy04,
        cd_dummy05: firstRow?.cd_dummy05,
        nm_dummy01: firstRow?.nm_dummy01,
        nm_dummy02: firstRow?.nm_dummy02,
        nm_dummy03: firstRow?.nm_dummy03,
        nm_dummy04: firstRow?.nm_dummy04,
        nm_dummy05: firstRow?.nm_dummy05,
      };

      return { ...defaults, ...baseRow };
    };

    const onAddRow = async () => {
      const newRow = buildDefaultRow();
      gridRef.current?.addRow(newRow);
    };

    const onDeleteRow = async () => {
      const api = gridRef.current?.api;
      if (!api) return;
      let selectedNodes = api.getSelectedNodes?.() || [];
      if (!selectedNodes.length) {
        const focused = api.getFocusedCell?.();
        if (focused && focused.rowIndex != null) {
          const node = api.getDisplayedRowAtIndex?.(focused.rowIndex);
          if (node) selectedNodes = [node];
        }
      }
      if (!selectedNodes.length) {
        await showDialog("alter", "선택된 행이 없습니다.");
        return;
      }

      const toRemove: any[] = [];
      const changed: string[] = [];

      selectedNodes.forEach((node: any) => {
        const row = node?.data || {};
        if (row.state === "insert") {
          toRemove.push(row);
        } else {
          const updated = { ...row, fg_eco: "D" };
          node.setData?.(updated);
          changed.push(`row:${node.rowIndex ?? ""} fg_eco -> D`);
        }
      });

      if (toRemove.length) api.applyTransaction?.({ remove: toRemove });

      if (changed.length) {
        //await showDialog("alter", changed.join("\n"));
      }
    };

    const onModifyRow = async () => {
      const api = gridRef.current?.api;
      if (!api) return;
      let selectedNodes = api.getSelectedNodes?.() || [];
      if (!selectedNodes.length) {
        const focused = api.getFocusedCell?.();
        if (focused && focused.rowIndex != null) {
          const node = api.getDisplayedRowAtIndex?.(focused.rowIndex);
          if (node) selectedNodes = [node];
        }
      }
      if (!selectedNodes.length) {
        await showDialog("alter", "선택된 행이 없습니다.");
        return;
      }

      // 신규(state === "insert")가 포함되어 있으면 경고 후 중단
      const hasInsert = selectedNodes.some(
        (node: any) => (node.data || {}).state === "insert"
      );
      if (hasInsert) {
        await showDialog("alter", "ECO구분을 확인하세요.");
        return;
      }

      const changed: string[] = [];
      selectedNodes.forEach((node: any) => {
        const row = { ...(node.data || {}) };
        row.fg_eco = "U";
        node.setData?.(row);
        changed.push(`row:${node.rowIndex ?? ""} fg_eco -> U`);
      });

      // 변경수량(qt_new) 칼럼으로 포커스 이동
      const firstNode = selectedNodes[0];
      const rowIndex = firstNode?.rowIndex ?? 0;
      api.setFocusedCell?.(rowIndex, "qt_new");
      api.ensureIndexVisible?.(rowIndex);

      if (changed.length) {
        // await showDialog("alter", changed.join("\n"));
      }
    };
    const onSearch = async () => {
      try {
        debugger;
        const paramSeed = {
          ...(initialParams || {}),
          ...(modalRef?.params || {}),
          nm_pjt:
            (modalRef?.params?.nm_pjt ?? initialParams?.nm_pjt ?? "") || "",
          nm_st_pjt:
            (modalRef?.params?.nm_st_pjt ?? initialParams?.nm_st_pjt ?? "") ||
            "",
        };
        const baseParams = {
          cd_pjt: paramSeed.cd_pjt,
          cd_unit: paramSeed.cd_unit,
          cd_item: paramSeed.cd_item,
          nm_pjt: paramSeed.nm_pjt,
          nm_st_pjt: paramSeed.nm_st_pjt,
          nm_item_old: paramSeed.nm_item_old,
        };
        const formParams = searchRef.current?.getFormdata?.() ?? {};
        const safeInitialParams = Object.fromEntries(
          Object.entries(paramSeed).filter(
            ([, v]) => v !== undefined && v !== null
          )
        );
        const params = {
          id: "sn_pms_pjt_reg_004_pop_002_01",
          params: {
            ...baseParams,
            ...formParams,
            cd_firm: userInfo?.cd_firm,
          },
        };
        const data: any = await fnGetDataSet(params);
        // data[0]: freeform, data[1]: grid
        if (data?.[0]?.[0]) {
          setFormData((prev: any) => {
            const mergedForm = {
              ...prev,
              ...data[0][0], // 조회값 우선 반영
              ...safeInitialParams, // 동일 키는 초기 파라미터(정의된 값만) 최종 유지
            };
            // FreeForm/Search 내부 상태도 즉시 반영
            freeFormRef.current?.setFormdata?.(mergedForm);
            searchRef.current?.setFormdata?.(mergedForm);
            return mergedForm;
          });
        }
        setGridData(data?.[1] || []);
      } catch (error) {}
    };

    const toBig = (v: any) => {
      const n = Number(v ?? 0);
      return isNaN(n) ? 0 : n;
    };

    const onConfirm = async () => {
      gridRef.current?.stopEditing?.(); // 편집 중인 셀 커밋
      const freeformValues = freeFormRef.current?.getFormdata?.() ?? {};
      const searchValues = searchRef.current?.getFormdata?.() ?? {};
      // gridRef.getRowData가 최신값을 반영하지 않는 경우가 있어 node 순회로 보강
      const rows: any[] = [];
      if (gridRef.current?.api?.forEachNode) {
        gridRef.current.api.forEachNode((node: any) => {
          if (node?.data) rows.push({ ...node.data });
        });
      } else {
        rows.push(...(gridRef.current?.getRowData?.() ?? gridData ?? []));
      }

      const cd_pjt = freeformValues.cd_pjt || searchValues.cd_pjt;
      if (!cd_pjt) {
        await showDialog("alter", "프로젝트를 입력해주세요.");
        return false;
      }

      const header = {
        cd_firm: userInfo?.cd_firm,
        cd_pjt,
        cd_unit: freeformValues.cd_unit || searchValues.cd_unit,
        cd_item: freeformValues.cd_item || searchValues.cd_item,
        no_hst: toBig(freeformValues.no_hst || 0),
        no_rev: freeformValues.no_rev || "0",
        fg_revision: freeformValues.fg_revision,
        dc_revision: freeformValues.dc_revision,
        dc_detail: freeformValues.dc_detail,
        yn_use: freeformValues.yn_use || "Y",
        qt_weight: toBig(freeformValues.qt_weight || 0),
        cd_user_reg: userInfo?.cd_user,
      };

      const lines =
        rows?.map((r: any) => ({
          cd_firm: userInfo?.cd_firm,
          cd_pjt: cd_pjt,
          cd_unit: r.cd_unit || header.cd_unit,
          cd_item: r.cd_item || header.cd_item,
          no_hst: toBig(r.no_hst || header.no_hst || 0),
          no_line: toBig(r.no_line),
          cd_matl: r.cd_matl,
          no_rev: r.no_rev || header.no_rev,
          qt_just: toBig(r.qt_new || r.qt_old || 0),
          qt_need: toBig(r.qt_new || r.qt_old || 0),
          rt_loss: toBig(r.rt_loss || 0),
          dc_rmk: r.dc_rmk,
          fg_eco: r.fg_eco,
          no_position: toBig(r.no_position || 0),
          no_seq: toBig(r.no_seq || 0),
          cd_cb: r.cd_cb,
          no_cbs: r.no_cbs,
          dc_rmk2: r.dc_rmk2,
          yn_approve: r.yn_approve || "N",
          dc_material: r.dc_material,
          dc_proc_af: r.dc_proc_af,
          cd_user_reg: userInfo?.cd_user,
        })) || [];

      const payload = {
        pms_pjt_ebomh: {
          insert: [header],
          update: [],
          delete: [],
        },
        pms_pjt_eboml: {
          insert: lines,
          update: [],
          delete: [],
        },
      };

      try {
        const ok = await fnSave("SN_PMS_PJT_REG_004_SAVE_02", payload);
        if (!ok) {
          return false;
        }
        return true;
      } catch (e: any) {
        await showDialog("error", e?.message || "저장 중 오류가 발생했습니다.");
        return false;
      }
    };

    const onCancel = () => {
      modalRef?.current?.handleConfirm?.(false, null);
    };

    const onResizeStopPanel = (seq: number, newSize: number) => {
      const key = seq === 1 ? "panel1" : "panel2";
      setPanelSizes((prev) => ({ ...prev, [key]: newSize }));
    };

    return (
      <div className="h-full flex flex-col">
        <div className="mb-1 shrink-0">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={searchControls}
            
          />
        </div>
        <div className="flex-1 h-full min-h-0 space-y-1.5">
          <SnResizable
            direction={"height"}
            seq={1}
            cd_menu={Menuprops?.cd_menu || "SN_PMS_PJT_REG_004_POP_002"}
            defaultSize={defaultPanelSize}
            size={panelSizes.panel1}
            onResizeStop={onResizeStopPanel}
            enable={{ bottom: true }}
            minHeight={250}
          >
            <div className="h-full overflow-hidden">
              <FreeForm
                ref={freeFormRef}
                data={formData}
                controls={freeform1Controls}
                
              />
            </div>
          </SnResizable>

          <SnResizable
            direction={"height"}
            seq={2}
            cd_menu={Menuprops?.cd_menu || "SN_PMS_PJT_REG_004_POP_002"}
            defaultSize={defaultPanelSize}
            size={panelSizes.panel2}
            onResizeStop={onResizeStopPanel}
            enable={{ bottom: true }}
            minHeight={150}
          >
            <div className="h-full overflow-hidden">
              <SnGrid
                ref={gridRef}
                menuInfo={modalRef}
                enableAddRow={true}
                columnConfig={gridColumns}
                rowData={gridData}
                isEditable={true}
                title="BOM 정보 "
                header={
                  <div className="space-x-2">
                    <SnButton
                      text="추가"
                      onClick={() => onGrid1BtnsClick("addRow")}
                    />
                    <SnButton
                      text="삭제"
                      onClick={() => onGrid1BtnsClick("deleteRow")}
                    />
                    <SnButton
                      text="수정"
                      onClick={() => onGrid1BtnsClick("modifyRow")}
                    />
                  </div>
                }
                onBeforeAddRow={(row: any) => {
                  const merged = buildDefaultRow(row || {});
                  Object.assign(row, merged);
                  return true;
                }}
              />
            </div>
          </SnResizable>
        </div>
      </div>
    );
  }
);

export default SN_PMS_PJT_REG_004_POP_002;
